import { useState } from 'react';
import { WIDGET_COLORS } from '../../constants/colors';
import {
  formatPoints,
  formatCurrency,
  downPaymentPercent,
  formatContactMonth,
} from '../../utils/formatSavingsPoints';
import type { SavingsPointsData } from '../../hooks/useGetSavingsPoints';
import { WidgetCardHeader } from '../widgetCardHeader';
import { WidgetDivider } from '../widgetDivider';
import { ProgressItem } from '../progressItem';
import { StatRow } from '../statRow';
import { WidgetNotice } from '../widgetNotice';
import { InfoButton } from '../infoButton';
import { EligibilityInfoModal } from '../eligibilityInfoModal';

type Props = { data: SavingsPointsData };

export function NearContact({ data }: Props) {
  const [infoOpen, setInfoOpen] = useState(false);
  const investmentNum = parseFloat(data.total_investment);
  const additionalNum = parseFloat(data.additional_downpayment_needed);
  const dpTarget = (investmentNum + additionalNum).toString();
  const dpPercent = downPaymentPercent(data.total_investment, dpTarget);

  return (
    <>
      <WidgetCardHeader
        variant="success"
        badgeText="Active · Soon"
        description="We will be in contact very soon about your home finance application!"
        descriptionColor={WIDGET_COLORS.green.text}
        descriptionWeight={600}
      />

      <WidgetDivider />

      <ProgressItem
        label="Community Points"
        value={
          <span className="font-bold" style={{ color: WIDGET_COLORS.green.text }}>
            ✓ {formatPoints(data.savings_points_earned)} of{' '}
            {formatPoints(data.savings_points_required)} required
          </span>
        }
        barPercent={100}
        barColor={WIDGET_COLORS.green.bar}
      />

      <ProgressItem
        label="Down Payment (GYS Home balance)"
        value={
          <span className="font-bold" style={{ color: WIDGET_COLORS.green.text }}>
            ✓ {formatCurrency(data.total_investment)} of {formatCurrency(dpTarget)}
          </span>
        }
        barPercent={dpPercent}
        barColor={WIDGET_COLORS.green.bar}
      />

      <StatRow
        bg={WIDGET_COLORS.green.bg}
        dividerColor={WIDGET_COLORS.green.divider}
        left={{
          value: `${formatPoints(data.savings_points_earned)} pts`,
          valueColor: WIDGET_COLORS.green.text,
          label: 'Community Points',
        }}
        right={{
          value: data.contact_month ? formatContactMonth(data.contact_month) : '-',
          valueColor: WIDGET_COLORS.green.text,
          label: (
            <span className="inline-flex items-center font-bold uppercase" style={{ color: WIDGET_COLORS.green.text }}>
              Within 3 months!
              <InfoButton
                onClick={() => setInfoOpen(true)}
                style={{ borderColor: WIDGET_COLORS.green.text, color: WIDGET_COLORS.green.text }}
              />
            </span>
          ),
        }}
      />

      <EligibilityInfoModal open={infoOpen} onOpenChange={setInfoOpen} />

      <WidgetNotice variant="success" icon="home">
        You have earned your target Community Points and saved up your down payment. Make sure your
        details are up to date and we&apos;ll be in contact soon!
      </WidgetNotice>
    </>
  );
}
